import "@/App.css";
import "@/auth.css";
import axios from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Menu, Settings2, Users } from "lucide-react";
import DashboardStats from "./components/DashboardStats";
import ExpenseChart from "./components/ExpenseChart";
import TransactionForm from "./components/TransactionForm";
import TransactionList from "./components/TransactionList";
import RateSettings from "./components/RateSettings";
import GroupManager from "./components/GroupManager";
import RecurringManager from "./components/RecurringManager";
import TrendChart from "./components/TrendChart";
import PdfReportButton from "./components/PdfReportButton";
import EmailReportButton from "./components/EmailReportButton";
import ActivityLog from "./components/ActivityLog";
import CollectionsPanel from "./components/CollectionsPanel";
import InvoicesPanel from "./components/InvoicesPanel";
import InvoiceSettings from "./components/InvoiceSettings";
import InvoiceCustomers from "./components/InvoiceCustomers";
import ErpImportPanel from "./components/ErpImportPanel";
import ErpSyncStatus from "./components/ErpSyncStatus";
import ErpSyncAlert from "./components/ErpSyncAlert";
import YearlyChart from "./components/YearlyChart";
import DescriptionGrouper from "./components/DescriptionGrouper";
import AccountManager from "./components/AccountManager";
import PayerManager from "./components/PayerManager";
import PayeeManager from "./components/PayeeManager";
import PayerSummary from "./components/PayerSummary";
import AccountSummary from "./components/AccountSummary";
import CurrencyBar from "./components/CurrencyBar";
import LoginPage from "./components/LoginPage";
import UserMenu from "./components/UserMenu";
import UserManager from "./components/UserManager";
import { useAuth } from "./context/AuthContext";
import { useIsMobile } from "./lib/useIsMobile";
import { accountShareTry, allocationsOf, currentMonth, tryValue } from "./lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function App() {
  const { user, ready, can, isAdmin } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [collections, setCollections] = useState([]);
  const [displayCurrency, setDisplayCurrency] = useState("TRY");
  const [groups, setGroups] = useState({ income: [], expense: [] });
  const [accounts, setAccounts] = useState([]);
  const [payers, setPayers] = useState([]);
  const [payees, setPayees] = useState([]);
  const [collectionFilters, setCollectionFilters] = useState({ payer: "", payee: "" });
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [collectionsError, setCollectionsError] = useState("");
  const loadId = useRef(0);
  const [month, setMonth] = useState(currentMonth());
  const [allTime, setAllTime] = useState(false);
  const [rate, setRate] = useState(0);
  const [fxRates, setFxRates] = useState({});
  const [rateSource, setRateSource] = useState("yükleniyor");
  const [filters, setFilters] = useState({ kind: "", group: "", account: "" });
  const [version, setVersion] = useState(0);
  const [view, setView] = useState("overview");
  const [menuOpen, setMenuOpen] = useState(false);
  const isMobile = useIsMobile();

  const canView = can("view");

  const load = useCallback(async () => {
    const requestId = ++loadId.current;
    setCollectionsLoading(true);
    setCollectionsError("");
    setVersion(v => v + 1);
    const query = allTime ? "" : `?month=${month}`;
    try {
    const [tx, gr, rt, col, acc, pay, received] = await Promise.all([
      axios.get(`${API}/transactions${query}`),
      axios.get(`${API}/groups`),
      axios.get(`${API}/rates/usd-try?prefer_manual=true`).catch(() => ({ data: { rate: 0, rates: {}, source: "manuel kur gerekli" } })),
      axios.get(`${API}/collections${query}`),
      axios.get(`${API}/accounts`).catch(() => ({ data: [] })),
      axios.get(`${API}/payers`).catch(() => ({ data: [] })),
      axios.get(`${API}/payees`),
    ]);
    if (requestId !== loadId.current) return;
    setAccounts(acc.data);
    setPayers(pay.data);
    setPayees(received.data);
    setTransactions(tx.data);
    setCollections(col.data);
    setGroups({ income: gr.data.income, expense: gr.data.expense, items: gr.data.items });
    setRate(rt.data.rate);
    setFxRates(rt.data.rates || {});
    setRateSource(rt.data.source);
    } catch (error) {
      if (requestId === loadId.current) {
        setCollections([]);
        setCollectionsError("Tahsilat bilgileri yüklenemedi. Lütfen tekrar deneyin.");
      }
    } finally {
      if (requestId === loadId.current) setCollectionsLoading(false);
    }
  }, [month, allTime]);

  useEffect(() => { if (user && canView) load().catch(() => {}); }, [user, canView, load]);

  const accountMatch = useCallback(value => {
    if (!filters.account) return true;
    if (filters.account === "__none__") return !value;
    return value === filters.account;
  }, [filters.account]);

  const visible = useMemo(
    () => transactions.filter(x =>
      (!filters.kind || x.kind === filters.kind) &&
      (!filters.group || x.group === filters.group) &&
      (x.kind !== "income" || accountMatch(x.account))),
    [transactions, filters, accountMatch]
  );

  const visibleCollections = useMemo(() => collections.filter(c => {
    const allocs = allocationsOf(c);
    if (!filters.account) return true;
    if (filters.account === "__none__") return !allocs.length;
    return allocs.some(a => a.account === filters.account);
  }), [collections, filters.account]);

  const partyCollections = useMemo(() => collections.filter(c =>
    Object.entries(collectionFilters).every(([field, value]) =>
      !value || (value === "__none__" ? !c[field] : c[field] === value))), [collections, collectionFilters]);
  const filteredCollections = useMemo(() => visibleCollections.filter(c =>
    Object.entries(collectionFilters).every(([field, value]) =>
      !value || (value === "__none__" ? !c[field] : c[field] === value))), [visibleCollections, collectionFilters]);

  const incomeByAccount = useMemo(
    () => transactions.reduce((acc, x) => {
      if (x.kind !== "income" || !x.account) return acc;
      acc[x.account] = (acc[x.account] || 0) + tryValue(x, rate);
      return acc;
    }, {}),
    [transactions, rate]
  );

  const totals = useMemo(
    () => {
      const base = visible.reduce((a, x) => {
        a[x.kind] += tryValue(x, rate);
        if (x.kind === "expense" && x.paid !== false) a.expense_paid += tryValue(x, rate);
        if (x.kind === "income") a.gross += tryValue({ ...x, amount: x.gross_revenue ?? x.amount }, rate);
        return a;
      }, { income: 0, expense: 0, expense_paid: 0, gross: 0 });
      base.collected = filters.account && filters.account !== "__none__"
        ? collections.reduce((sum, c) => sum + accountShareTry(c, filters.account, rate), 0)
        : visibleCollections.reduce((sum, c) => sum + tryValue(c, rate), 0);
      return base;
    },
    [visible, visibleCollections, collections, filters.account, rate]
  );

  const breakdown = useCallback(
    kind => (groups[kind] || [])
      .map(group => ({
        group,
        total: visible.filter(x => x.kind === kind && x.group === group).reduce((s, x) => s + tryValue(x, rate), 0),
      }))
      .filter(x => x.total),
    [groups, visible, rate]
  );
  const chart = useMemo(() => ({ expense: breakdown("expense"), income: breakdown("income") }), [breakdown]);
  const allGroups = useMemo(() => [...new Set([...groups.expense, ...groups.income])], [groups]);

  const monthLabel = allTime ? "Tüm zamanlar" : new Date(`${month}-01`).toLocaleDateString("tr-TR", { month: "long", year: "numeric" });

  const navItems = [
    { key: "overview", label: "Genel Bakış", show: true },
    { key: "transactions", label: "İşlemler", show: true },
    { key: "collections", label: "Tahsilat", show: true },
    { key: "invoices", label: "Faturalar", show: true },
    { key: "erp", label: "Satış Aktarımı", show: can("create") },
    { key: "reports", label: "Raporlar", show: true },
    { key: "settings", label: "Tanımlar", show: can("groups") || can("rates") || can("create") },
    { key: "users", label: "Kullanıcılar", show: isAdmin },
  ].filter(item => item.show);
  const activeLabel = navItems.find(item => item.key === view)?.label || "Genel Bakış";

  if (!ready) return <div className="auth-loading" data-testid="auth-loading">Yükleniyor…</div>;
  if (!user) return <LoginPage/>;

  return (
    <div className="shell">
      <aside>
        <div className="brand-row">
          <div className="brand">
            <div className="brand-card">
              <img src="/roimax-logo.png" alt="Roimax" className="brand-logo" data-testid="sidebar-logo"/>
              <span className="brand-sub">finans paneli</span>
            </div>
          </div>
          <button type="button" className="menu-toggle" data-testid="mobile-menu-toggle"
            aria-expanded={menuOpen} aria-label="Menüyü aç/kapat" onClick={() => setMenuOpen(open => !open)}>
            <Menu size={18}/>
            <span>{activeLabel}</span>
            <ChevronDown size={16} className={menuOpen ? "flip" : ""}/>
          </button>
        </div>
        <nav className={menuOpen ? "open" : ""} data-testid="sidebar-nav">
          {navItems.map(item => (
            <a key={item.key} className={view === item.key ? "active" : ""} data-testid={`nav-${item.key}`}
              onClick={() => { setView(item.key); setMenuOpen(false); }}>{item.label}</a>
          ))}
          {isMobile && <div className="nav-user" data-testid="mobile-user-menu"><UserMenu/></div>}
        </nav>
        <div className="sidebar-note">
          {isAdmin ? <Users size={18}/> : <Settings2 size={18}/>}
          <span>{isAdmin ? <>Kullanıcı yetkileri<br/><small>sen yönetiyorsun</small></> : <>Kur ve tanımlar<br/><small>Tanımlar menüsünde</small></>}</span>
        </div>
      </aside>
      <main>
        <header className="topbar">
          <div>
            <p className="eyebrow">ROIMAX FİNANS</p>
            <h1>{activeLabel}</h1>
          </div>
          <div className="topbar-actions">
            {view !== "users" && <CurrencyBar value={displayCurrency} onChange={setDisplayCurrency} rates={fxRates}/>}
            {view !== "users" && (
              <div className="month-picker">
                <label htmlFor="month">Dönem</label>
                <input id="month" data-testid="month-filter" type="month" value={month} disabled={allTime} onChange={e => setMonth(e.target.value)}/>
                <button type="button" className={`all-time-btn ${allTime ? "on" : ""}`} data-testid="all-time-toggle"
                  title="Tüm kayıtları dönem filtresi olmadan göster" onClick={() => setAllTime(v => !v)}>
                  {allTime ? "Tüm zamanlar · açık" : "Tüm zamanlar"}
                </button>
              </div>
            )}
            {!isMobile && <UserMenu/>}
          </div>
        </header>

        {view === "users" ? <UserManager/> : !canView ? (
          <div className="no-permission" data-testid="no-view-permission">
            Hesabınıza henüz görüntüleme yetkisi verilmemiş. Lütfen yöneticinizle iletişime geçin.
          </div>
        ) : (
          <>
            {view === "overview" && (
              <>
                {can("create") && <ErpSyncAlert refreshKey={version} onAck={load}/>}
                <section className="hero">
                  <div>
                    <p className="eyebrow">AYLIK FİNANS ÖZETİ</p>
                    <h2>{monthLabel}</h2>
                    <p>Gelir, gider ve tahsilatını tek yerde, daha net gör.</p>
                  </div>
                  <div className="rate-chip" data-testid="exchange-rate-info">
                    <span>1 USD =</span>
                    <strong>{rate ? `${rate.toFixed(2)} TL` : "—"}</strong>
                    {fxRates.AED > 0 && <span className="aed-line" data-testid="aed-rate-info">1 AED = <b>{fxRates.AED.toFixed(4)} TL</b> · 1 USD = <b>{(rate / fxRates.AED).toFixed(4)} AED</b></span>}
                    {fxRates.EUR > 0 && <span className="aed-line" data-testid="eur-rate-info">1 EUR = <b>{fxRates.EUR.toFixed(4)} TL</b> · 1 EUR = <b>{(fxRates.EUR / rate).toFixed(4)} USD</b></span>}
                    <small>{rateSource}</small>
                  </div>
                </section>

                <DashboardStats totals={totals} count={visible.length} month={monthLabel} currency={displayCurrency} rates={fxRates}/>

                <AccountSummary transactions={transactions} collections={collections} accounts={accounts} rate={rate}
                  currency={displayCurrency} rates={fxRates} activeAccount={filters.account}
                  onSelect={account => setFilters({ ...filters, account: account === "Atanmamış" ? "__none__" : account })}/>

                <div className="content-grid single">
                  <ExpenseChart data={chart} currency={displayCurrency} rates={fxRates}/>
                </div>
              </>
            )}

            {view === "transactions" && (
              <>
                <div className={can("create") ? "content-grid" : "content-grid single"}>
                  <ExpenseChart data={chart} currency={displayCurrency} rates={fxRates}/>
                  {can("create") && <TransactionForm groups={groups} accounts={accounts} onCreated={load}/>}
                </div>

                <TransactionList transactions={visible} filters={filters} setFilters={setFilters} groups={groups} allGroups={allGroups}
                  accounts={accounts} month={month} allTime={allTime} rate={rate} currency={displayCurrency} rates={fxRates} onChanged={load}/>

                {can("edit") && <DescriptionGrouper groups={groups} onChanged={load}/>}
              </>
            )}

            {view === "collections" && (
              <>
                <CollectionsPanel collections={filteredCollections} incomes={transactions.filter(x => x.kind === "income")}
                  accounts={accounts} payers={payers} payees={payees} incomeByAccount={incomeByAccount}
                  exportMonth={allTime ? "" : month} filters={{ ...collectionFilters, account: filters.account }}
                  onFiltersChange={({ payer, payee, account }) => { setCollectionFilters({ payer, payee }); setFilters(current => ({ ...current, account })); }}
                  loading={collectionsLoading} error={collectionsError}
                  month={monthLabel} rate={rate} currency={displayCurrency} rates={fxRates} onChanged={load}/>

                <PayerSummary collections={collectionsLoading ? [] : filteredCollections} rate={rate} currency={displayCurrency} rates={fxRates}/>

                <AccountSummary transactions={transactions} collections={collectionsLoading ? [] : partyCollections} accounts={accounts} rate={rate}
                  currency={displayCurrency} rates={fxRates} activeAccount={filters.account}
                  onSelect={account => setFilters({ ...filters, account: account === "Atanmamış" ? "__none__" : account })}/>
              </>
            )}

            {view === "invoices" && <InvoicesPanel month={month} allTime={allTime} monthLabel={monthLabel}/>}

            {view === "erp" && (() => {
              const erpMonth = allTime ? new Date().toISOString().slice(0, 7) : month;
              const erpLabel = new Date(`${erpMonth}-01`).toLocaleDateString("tr-TR", { month: "long", year: "numeric" });
              return (
                <>
                  <ErpSyncStatus onSynced={load}/>
                  <ErpImportPanel month={erpMonth} monthLabel={erpLabel} onImported={load}/>
                </>
              );
            })()}

            {view === "reports" && (
              <>
                {can("reports") && (
                  <section className="panel report-actions" data-testid="report-actions">
                    <div className="panel-head">
                      <div>
                        <p className="eyebrow">AYLIK RAPOR</p>
                        <h3>{monthLabel} raporu</h3>
                      </div>
                      <div className="actions">
                        {!allTime && <PdfReportButton month={month}/>}
                        {!allTime && <EmailReportButton month={month} onSent={load}/>}
                      </div>
                    </div>
                    {allTime && <small className="hint">PDF ve e-posta raporu için "Tüm zamanlar" filtresini kapatıp bir dönem seçin.</small>}
                  </section>
                )}

                <TrendChart month={month} refreshKey={version}/>

                <YearlyChart month={month} refreshKey={version}/>

                <ActivityLog refreshKey={version} onChanged={load}/>
              </>
            )}

            {view === "settings" && (
              <>
                {(can("groups") || can("rates")) && (
                  <div className="content-grid two">
                    {can("groups") && <GroupManager groups={groups} onCreated={load}/>}
                    {can("rates") && <RateSettings month={month} onSaved={load}/>}
                  </div>
                )}

                {can("groups") && (
                  <div className="content-grid two">
                    <AccountManager accounts={accounts} onChanged={load}/>
                    <PayerManager payers={payers} onChanged={load}/>
                    <PayeeManager payees={payees} onChanged={load}/>
                  </div>
                )}

                {can("groups") && (
                  <div className="content-grid two">
                    <InvoiceSettings/>
                    <InvoiceCustomers/>
                  </div>
                )}

                {can("create") && <RecurringManager refreshKey={version} onChanged={load}/>}
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}
